import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MarketplaceLayout } from "../components/MarketplaceLayout";
import { useAuth } from "../contexts/AuthContext";
import api, { fileUrl } from "../lib/api";
import {
  ShieldCheck,
  Printer,
  MapPin,
  Hand,
  PenLine,
  Sparkles,
  Layers,
  XCircle,
  ArrowRight,
  Leaf,
} from "lucide-react";

const HERO_FALLBACK = "https://images.unsplash.com/photo-1561839561-b13bcfe95249?auto=format&fit=crop&w=1600&q=80";
const WORKSHOP_FALLBACK = "https://images.unsplash.com/photo-1611532736597-de2d4265fba3?auto=format&fit=crop&w=1200&q=80";

export default function SellPage() {
  const { user } = useAuth();
  const [heroImg, setHeroImg] = useState(HERO_FALLBACK);

  useEffect(() => {
    api.get("/settings/public").then((r) => {
      if (r.data?.hero_workshop_image) setHeroImg(fileUrl(r.data.hero_workshop_image));
    }).catch(() => {});
  }, []);

  const cta = user?.role === "artist" ? "/artist/upload" : user ? "/artist" : "/register?role=artist";
  const ctaLabel = user?.role === "artist" ? "Subir una obra" : "Empezar a vender";

  return (
    <MarketplaceLayout>
      {/* HERO */}
      <section className="relative pt-20 pb-20 lg:pt-32 lg:pb-32" data-testid="sell-hero">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-neutral-500 mb-8 flex items-center gap-3">
              <span className="inline-block w-8 h-px bg-neutral-400"></span>
              Para artistas
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light tracking-tighter leading-[0.95]">
              Vende tu obra sin riesgo.<br />
              <span className="italic text-neutral-500">Conserva el control.</span>
            </h1>
            <p className="mt-10 text-base sm:text-lg text-neutral-600 max-w-xl leading-relaxed">
              Una galería 100% online y <strong className="text-neutral-900">print-on-demand</strong>. No produces antes de vender, no almacenas obra, no inviertes nada. Tú marcas el precio, nosotros nos encargamos de la impresión fine art, embalaje y envío al cliente.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-5">
              <Link to={cta} className="bg-black text-white px-10 py-4 overline hover:bg-neutral-800 transition-colors flex items-center gap-3 group" data-testid="sell-cta-primary">
                {ctaLabel} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <a href="#como-funciona" className="border border-black text-black px-10 py-4 overline hover:bg-neutral-100 transition-colors" data-testid="sell-cta-secondary">
                Cómo funciona
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden bg-neutral-100">
              <img src={heroImg} alt="Taller de impresión fine art" className="w-full h-full object-cover" />
            </div>
            <div className="mt-4 text-xs text-neutral-500 italic">Impreso a 5 km de tu casa, en Alicante.</div>
          </div>
        </div>
      </section>

      {/* RIESGO CERO */}
      <section className="bg-neutral-950 text-neutral-100 py-20 lg:py-28" data-testid="sell-noric-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="overline text-neutral-400 mb-6">N° 01 · Riesgo cero</div>
          <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight max-w-3xl leading-tight">
            Solo ganas <span className="italic text-neutral-300">cuando se vende</span>. Y antes de eso, no inviertes nada.
          </h2>
          <div className="mt-16 grid md:grid-cols-3 gap-px bg-neutral-800">
            {[
              {
                icon: ShieldCheck,
                title: "Cero producción previa",
                desc: "No imprimimos hasta que un cliente compra. Tu archivo digital queda en custodia, no hay stock ni costes de almacenaje.",
              },
              {
                icon: XCircle,
                title: "Cero gastos iniciales",
                desc: "Subir tu obra es gratis. Solo cobramos el 20% de comisión sobre las ventas reales. Si no se vende, no pagas nada.",
              },
              {
                icon: PenLine,
                title: "Tú marcas tu precio",
                desc: "Decides cuánto quieres ganar neto por venta. Calculamos el PVP final transparentemente, sin sorpresas.",
              },
            ].map((it, i) => (
              <div key={i} className="bg-neutral-950 p-10 lg:p-12">
                <it.icon size={28} strokeWidth={1.2} className="text-neutral-300" />
                <h3 className="font-serif text-2xl mt-6 text-white">{it.title}</h3>
                <p className="text-sm text-neutral-400 mt-4 leading-relaxed">{it.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CÓMO FUNCIONA */}
      <section id="como-funciona" className="py-24 lg:py-32" data-testid="sell-how-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <div className="overline text-neutral-500 mb-6">N° 02 · Cómo funciona</div>
          <h2 className="font-serif text-4xl lg:text-5xl font-light tracking-tight max-w-2xl leading-tight">
            Cuatro pasos. Tú creas. Nosotros producimos. El cliente recibe.
          </h2>

          <div className="mt-16 space-y-px bg-neutral-200">
            {[
              {
                n: "01",
                title: "Sube tu obra digital",
                desc: "Imagen en alta resolución, descripción, categoría. Auto-detectamos las dimensiones y te sugerimos tallas proporcionales con calidad mínima 200 DPI.",
              },
              {
                n: "02",
                title: "Decide tu edición y tu precio neto",
                desc: "¿Edición abierta o limitada (5, 25, 100…)? Marca cuánto quieres ganar tú por venta. Cuantas menos impresiones permitas, más exclusiva (y valiosa) será tu obra.",
              },
              {
                n: "03",
                title: "Aprobamos tu obra de forma curada",
                desc: "Revisamos calidad, derechos y encaje editorial. Si pasa, se publica. Si no, recibes feedback claro para corregir y volver a enviar.",
              },
              {
                n: "04",
                title: "Vendemos, imprimimos, enviamos",
                desc: "Cuando un cliente compra, lo imprimimos en nuestro taller de Alicante sobre papel etching 310gr o canvas 100% algodón 390gr, lo enviamos con certificado firmado por ti, y te liquidamos tu neto.",
              },
            ].map((s, i) => (
              <div key={i} className="bg-white p-8 lg:p-12 grid grid-cols-12 gap-6 items-start">
                <div className="col-span-12 lg:col-span-2 font-serif text-5xl text-neutral-300">{s.n}</div>
                <div className="col-span-12 lg:col-span-10">
                  <h3 className="font-serif text-2xl lg:text-3xl text-neutral-950">{s.title}</h3>
                  <p className="text-sm text-neutral-600 mt-3 leading-relaxed max-w-3xl">{s.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* EDICIÓN LIMITADA + EXCLUSIVIDAD */}
      <section className="bg-neutral-50 py-24 lg:py-32" data-testid="sell-edition-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-neutral-500 mb-6">N° 03 · Edición y exclusividad</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Menos impresiones,<br />
              <span className="italic">más valor</span>.
            </h2>
            <p className="mt-8 text-neutral-600 leading-relaxed">
              Tú decides cuántas veces se puede imprimir tu obra. Una edición limitada de 5 vale exponencialmente más que una abierta. Cada impresión se entrega con una tarjeta numerada y firmada por ti.
            </p>
            <div className="mt-6 p-5 bg-white border-l-2 border-black text-sm leading-relaxed text-neutral-700">
              Cuando una obra se aprueba, te pedimos que nos envíes (o vengas al taller a firmar) tantos certificados numerados como impresiones haya en tu edición. Los custodiamos y los enviamos con cada copia vendida.
            </div>
          </div>
          <div className="lg:col-span-7 space-y-px bg-neutral-200">
            {[
              {
                icon: Layers,
                title: "Edición limitada",
                desc: "Define un cupo (ej. 25 impresiones). Cuando se agota, la obra se retira automáticamente del catálogo. Más raro = más cotizado.",
              },
              {
                icon: Sparkles,
                title: "Compra en exclusiva",
                desc: "Un cliente puede llevarse el resto de la edición + un sobreprecio de exclusividad para retirarla por completo. Tu obra queda en una sola colección privada.",
              },
              {
                icon: Hand,
                title: "Retirada unilateral",
                desc: "Tú puedes retirar tu obra del catálogo cuando quieras desde tu dashboard. Y nosotros también, si dejara de encajar editorialmente. Sin penalizaciones, sin contratos cerrados.",
              },
            ].map((it, i) => (
              <div key={i} className="bg-neutral-50 p-8 lg:p-10 flex gap-6 items-start">
                <it.icon size={28} strokeWidth={1.2} className="text-neutral-900 shrink-0 mt-1" />
                <div>
                  <h3 className="font-serif text-2xl">{it.title}</h3>
                  <p className="text-sm text-neutral-600 mt-3 leading-relaxed">{it.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LOCAL · KM 0 · SOSTENIBLE */}
      <section className="py-24 lg:py-32" data-testid="sell-local-section">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <img
              src={WORKSHOP_FALLBACK}
              alt="Taller en Alicante"
              className="w-full aspect-[4/3] object-cover"
            />
          </div>
          <div className="lg:col-span-6">
            <div className="overline text-neutral-500 mb-6">N° 04 · Compromiso local</div>
            <h2 className="font-serif text-4xl lg:text-5xl font-light tracking-tight leading-tight">
              Artistas locales.<br />Impresión kilómetro cero.
            </h2>
            <p className="mt-8 text-neutral-600 leading-relaxed">
              d-Pod Gallery nace dentro de <strong className="text-neutral-900">123imprime</strong> y su marca de imprenta digital <strong className="text-neutral-900">d-impresión</strong>, un taller de impresión fine art en Alicante con más de 20 años imprimiendo para artistas y galerías. Trabajamos con bases mediterráneas: lienzo de algodón, papel etching, tintas pigmentadas archivables.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { icon: MapPin, label: "100% Alicante", val: "Taller propio, sin externalizar" },
                { icon: Leaf, label: "Print-on-demand", val: "Cero stock, cero residuo" },
                { icon: Printer, label: "Tintas archivables", val: "Permanencia 100+ años" },
                { icon: Hand, label: "Cultura local", val: "Talleres de marco asociados" },
              ].map((b, i) => (
                <div key={i} className="border-l-2 border-neutral-900 pl-4 py-1">
                  <div className="flex items-center gap-2 text-xs text-neutral-500 uppercase tracking-wider">
                    <b.icon size={12} strokeWidth={1.5} /> {b.label}
                  </div>
                  <div className="text-sm text-neutral-900 mt-1 font-serif">{b.val}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / SIN CONTRATOS */}
      <section className="bg-neutral-950 text-neutral-100 py-24 lg:py-32" data-testid="sell-cta-section">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 text-center">
          <div className="overline text-neutral-400 mb-8">Sin letra pequeña</div>
          <h2 className="font-serif text-4xl lg:text-6xl font-light tracking-tight leading-tight">
            Sin contratos cerrados.<br />
            <span className="italic text-neutral-300">Cualquiera de las partes puede retirarse cuando quiera.</span>
          </h2>
          <p className="mt-10 text-neutral-400 max-w-2xl mx-auto leading-relaxed">
            Tú puedes retirar tus obras del catálogo en cualquier momento desde tu dashboard. Nosotros también podemos retirarlas si dejaran de encajar editorialmente — siempre con aviso y explicación. Es un acuerdo de palabra entre creadores y un taller de impresión que respeta el oficio.
          </p>
          <div className="mt-12 flex flex-wrap justify-center gap-5">
            <Link to={cta} className="bg-white text-black px-10 py-4 overline hover:bg-neutral-100 transition-colors flex items-center gap-3 group" data-testid="sell-cta-final">
              {ctaLabel} <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
            </Link>
            <Link to="/" className="border border-neutral-600 text-neutral-200 px-10 py-4 overline hover:bg-neutral-900 transition-colors" data-testid="sell-cta-explore">
              Ver el catálogo
            </Link>
          </div>
        </div>
      </section>
    </MarketplaceLayout>
  );
}
